import { ReactNode, useState } from "react";
import { toast } from "sonner";
import {
  useSuspenseGqlQuery,
  useGqlMutation,
} from "@/lib/GraphQLCodegen/fetcher";
import { supplierThreadQuery } from "@/app/Threads/GraphQL/threadQueries";
import { createSupplierWorkOrderThreadMutation } from "@/app/Threads/GraphQL/threadMutations";
import { sendMessageMutation } from "@/app/Threads/GraphQL/threadMessageMutations";
import { ThreadClosedBanner } from "@/app/Threads/Components/ThreadClosedBanner";
import { ThreadPresenceProvider } from "@/app/Threads/Components/ThreadPresenceProvider";
import { ThreadActivityProvider } from "@/app/Threads/Components/ThreadActivityProvider";
import { ThreadMessagesView } from "@/app/Threads/Components/ThreadMessagesView";
import { MessageInput } from "@/app/Threads/Components/MessageInput";
import { MessageDropzone } from "@/app/Threads/Components/MessageDropzone";
import { useMessageAttachments } from "@/app/Threads/hooks/useMessageAttachments";
import {
  ClosedBadge,
  UnreadBadge,
} from "@/app/Threads/Components/ThreadHeader";
import { WorkOrdersFieldsFragment } from "@/gql/graphql";
import { hasUnread } from "@/app/Threads/utils/hasUnread";
import { cn } from "@/lib/utils";

const MESSAGES_CLASS_NAME = "px-4 pb-2 pt-4";
const INPUT_CLASS_NAME = "border-t-0 bg-gray-50 px-3 pb-3 pt-1";

function EmptyState() {
  return (
    <span className="rounded-full bg-white px-3 py-1 text-xs text-gray-500 shadow-sm">
      No messages yet. Start the thread.
    </span>
  );
}

export function WorkOrderSupplierThread({
  workOrder,
}: {
  workOrder: WorkOrdersFieldsFragment;
}) {
  const [justCreated, setJustCreated] = useState(false);

  return workOrder.supplier_thread_id ? (
    <SupplierThread
      threadId={workOrder.supplier_thread_id}
      autoFocus={justCreated}
    />
  ) : (
    <ThreadFrame>
      <NewSupplierThread
        workOrder={workOrder}
        onCreated={() => setJustCreated(true)}
      />
    </ThreadFrame>
  );
}

function ThreadFrame({
  badges,
  children,
}: {
  badges?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex h-full max-h-[600px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-gray-50">
      {badges ? (
        <div className="flex items-center gap-2 px-3 pt-3">{badges}</div>
      ) : null}

      {children}
    </div>
  );
}

function SupplierThread({
  threadId,
  autoFocus,
}: {
  threadId: string;
  autoFocus: boolean;
}) {
  const { data } = useSuspenseGqlQuery(supplierThreadQuery, { id: threadId });

  const thread = data.thread;
  const unread = hasUnread(thread);

  return (
    <ThreadPresenceProvider key={thread.id} threadId={thread.id}>
      <ThreadActivityProvider>
        <ThreadFrame
          badges={
            unread || thread.closed ? (
              <>
                {unread ? <UnreadBadge /> : null}
                {thread.closed ? <ClosedBadge /> : null}
              </>
            ) : null
          }
        >
          <ThreadClosedBanner closed={thread.closed} />
          <ThreadMessagesView
            id={thread.id}
            autoFocus={autoFocus}
            messagesClassName={MESSAGES_CLASS_NAME}
            inputClassName={INPUT_CLASS_NAME}
            emptyState={<EmptyState />}
          />
        </ThreadFrame>
      </ThreadActivityProvider>
    </ThreadPresenceProvider>
  );
}

/**
 * Renders the composer before a thread exists. The thread is created on the
 * first send so the panel looks and behaves the same either way.
 */
function NewSupplierThread({
  workOrder,
  onCreated,
}: {
  workOrder: WorkOrdersFieldsFragment;
  onCreated: () => void;
}) {
  const createThread = useGqlMutation(createSupplierWorkOrderThreadMutation, {
    onError: () => toast.error("Failed to create thread"),
  });
  const sendMessage = useGqlMutation(sendMessageMutation, {
    onError: () => toast.error("Failed to send message"),
  });
  const attachments = useMessageAttachments();

  async function handleSend(body: string, filePaths: string[]) {
    const created = await createThread
      .mutateAsync({ work_order_id: workOrder.id })
      .catch(() => null);

    if (!created) {
      return;
    }

    onCreated();

    await sendMessage
      .mutateAsync({
        thread_id: created.createSupplierWorkOrderThread.id,
        body: body || undefined,
        file_paths: filePaths.length > 0 ? filePaths : undefined,
      })
      .catch(() => null);
  }

  return (
    <MessageDropzone onDrop={attachments.add}>
      <div className={cn("flex-1 overflow-y-auto p-4", MESSAGES_CLASS_NAME)}>
        <div className="flex h-full items-center justify-center">
          <EmptyState />
        </div>
      </div>

      <MessageInput
        onSend={handleSend}
        attachments={attachments}
        autoFocus={false}
        allowNotes={false}
        className={INPUT_CLASS_NAME}
      />
    </MessageDropzone>
  );
}
